import "../../kendo.html.button.js";
import "../../kendo.icons.js";
import "../../kendo.promptbox.js";
import { Widget } from "../../core/base/widget.js";
import { utilsService } from "../../core/services/utils.service.js";
import { domUtilsService } from "../../core/services/dom-utils.service.js";
import { DragToScrollHandler } from "../../core/models/dom-utils.js";
import { NS, STYLES, REFERENCES, SCROLLING_DELTA, CLICK, CHANGE, DOT, INPUT, KEYDOWN, RTL, EVENTS, SUGGESTED_ACTIONS_LAYOUT_MODE } from "../constants.js";
import type { IChatOptions, IMessage, IChatMessages, IMessageBoxSettings } from "../models/index.js";
import { renderFiles, renderSuggestionsWithLayout } from "../templates/index.js";
import { IKendoJQueryStatic, IKendoJQuery } from "../../core/index.js";
import { PromptBox } from "../../promptbox/index.js";
import type { IFile } from "../../upload/index.js";

declare const kendo: any;
declare const $: IKendoJQueryStatic;

export interface IChatMessageBoxContext {
    chatElement: IKendoJQuery;
}

/**
 * MessageBox handles the input area of the chat component.
 * Delegates text input and file attachments entirely to PromptBox.
 *
 * This is an internal collaborator that extends Widget for event handling.
 */
export class MessageBox extends Widget {
    static readonly options = {
        name: "MessageBox"
    };

    events: string[] = [
        EVENTS.sendMessage,
        EVENTS.input,
        EVENTS.suggestionClick,
        EVENTS.replyMessageCloseButtonClick,
        EVENTS.fileSelect,
        EVENTS.fileRemove
    ];

    private chatElement!: IKendoJQuery;
    private _generating = false;
    private _typing = false;
    promptBoxInstance: any;
    private suggestions?: IKendoJQuery;
    private customMessageBox?: IKendoJQuery;
    private customInput?: IKendoJQuery;
    private customSendButton?: IKendoJQuery;
    private customFiles: IFile[] = [];
    private _customSendButtonHtml?: string;
    private _customSendButtonText?: string;

    private context!: IChatMessageBoxContext;
    private _dragHandler: DragToScrollHandler | null = null;

    get textAreaInstance(): any {
        return this.promptBoxInstance || this.customInput;
    }

    /**
     * Constructs a new MessageBox instance.
     */
    constructor(
        element: IKendoJQuery,
        options: Partial<IChatOptions>,
        context: IChatMessageBoxContext
    ) {
        super(element, $.extend(true, {}, MessageBox.options, options));

        this.context = context;
        this.chatElement = context.chatElement;
        this._generating = false;

        this._initWrapper();
        this._initPromptBox();
        this._attachEvents();
        this._typing = false;
    }

    /**
     * Creates the wrapper element for the message box.
     */
    private _initWrapper(): void {
        const options = this.options as IChatOptions;
        this.wrapper = $(`<div class="${STYLES.messageBoxWrapper}"></div>`);

        if (options.suggestions.length) {
            let suggestionsHtml: string;
            if (options.suggestionsTemplate) {
                suggestionsHtml = options.suggestionsTemplate(options.suggestions);
            } else {
                const layoutMode = this._getSuggestionsLayoutMode(options);
                suggestionsHtml = renderSuggestionsWithLayout(
                    options.suggestions,
                    layoutMode,
                    options.dir === RTL
                );
            }

            this.suggestions = this.wrapper!.append(suggestionsHtml);
        }

        this.wrapper!.find(`[${REFERENCES.leftScrollButton}]`).addClass(STYLES.disabled);
        this.chatElement.append(this.wrapper);
    }

    /**
     * Gets the effective suggestions layout mode, considering legacy options.
     */
    private _getSuggestionsLayoutMode(options: IChatOptions): typeof SUGGESTED_ACTIONS_LAYOUT_MODE[keyof typeof SUGGESTED_ACTIONS_LAYOUT_MODE] {
        if (options.suggestionsLayoutMode) {
            return options.suggestionsLayoutMode;
        }
        if (options.suggestionsScrollable) {
            return SUGGESTED_ACTIONS_LAYOUT_MODE.SCROLLBUTTONS;
        }
        return SUGGESTED_ACTIONS_LAYOUT_MODE.SCROLL;
    }

    /**
     * Initializes the PromptBox component for message input.
     */
    private _initPromptBox(): void {
        const options = this.options as IChatOptions;

        if (options.messageBoxTemplate) {
            this._initCustomMessageBox(options);
            return;
        }

        const messages = options.messages || {} as IChatMessages;
        const messageBox = options.messageBox || {} as IMessageBoxSettings;
        const actionButtonOpts = options.actionButton;
        const fileAttachmentEnabled = options.fileAttachment !== false;

        const mode = messageBox.mode || "auto";
        const maxTextAreaHeight = messageBox.maxTextAreaHeight;
        const rows = messageBox.rows || 1;
        const startAffixTemplate = messageBox.startAffixTemplate;
        const endAffixTemplate = messageBox.endAffixTemplate;
        const topAffixTemplate = messageBox.topAffixTemplate;

        const actionButtonSettings = this._getActionButtonSettings(actionButtonOpts);
        const speechToTextSettings = this._getSpeechToTextSettings(options);
        const fileAttachmentSettings = this._getFileAttachmentSettings(options, fileAttachmentEnabled);

        const promptBox = new PromptBox(this.element, {
            mode: mode,
            maxTextAreaHeight: maxTextAreaHeight,
            rows: rows,
            _filesTemplate: this._getInternalFilesTemplate(options),
            startAffixTemplate: startAffixTemplate,
            endAffixTemplate: endAffixTemplate,
            topAffixTemplate: topAffixTemplate,
            placeholder: messages.placeholder,
            speechToTextButton: speechToTextSettings,
            actionButton: actionButtonSettings,
            fileSelectButton: fileAttachmentSettings,
            messages: {
                placeholder: messages.placeholder,
                actionButton: messages.sendButton || actionButtonOpts?.text || messages.actionButton,
                actionButtonLoading: messages.sendButtonLoading || actionButtonOpts?.loadingText || actionButtonOpts?.stopText || messages.actionButtonLoading || messages.stopButton
            },
            input: this._input.bind(this),
            promptAction: this._promptBoxAction.bind(this),
            fileSelect: this._promptBoxFileSelect.bind(this),
            fileRemove: this._promptBoxFileRemove.bind(this)
        });

        promptBox.wrapper.appendTo(this.wrapper);

        this.promptBoxInstance = promptBox;
    }

    private _initCustomMessageBox(options: IChatOptions): void {
        const messages = options.messages || {} as IChatMessages;
        const messageBox = $(`<div class="${STYLES.messageBox}"></div>`);
        const templateHtml = options.messageBoxTemplate!();

        messageBox.append(templateHtml);
        this.wrapper!.append(messageBox);

        this.customMessageBox = messageBox;
        this.customInput = this._findCustomInput(messageBox);
        this.customSendButton = this._findCustomSendButton(messageBox);

        if (this.customInput?.length) {
            this.customInput.attr("aria-label", messages.messageBoxLabel);

            if (!this.customInput.attr("placeholder") && messages.placeholder) {
                this.customInput.attr("placeholder", messages.placeholder);
            }
        }

        if (this.customSendButton?.length) {
            const sendLabel = messages.sendButton || "Send message";
            this._customSendButtonHtml = this.customSendButton.html();
            this._customSendButtonText = this.customSendButton.text();

            if (!this.customSendButton.attr("aria-label")) {
                this.customSendButton.attr("aria-label", sendLabel);
            }

            if (!this.customSendButton.attr("title")) {
                this.customSendButton.attr("title", sendLabel);
            }
        }
    }

    /**
     * Gets action button settings to pass to PromptBox.
     */
    private _getActionButtonSettings(actionButtonOpts: any): object {
        return {
            enable: true,
            icon: actionButtonOpts?.icon,
            loadingIcon: actionButtonOpts?.loadingIcon || actionButtonOpts?.stopIcon,
            fillMode: actionButtonOpts?.fillMode,
            rounded: actionButtonOpts?.rounded,
            size: actionButtonOpts?.size,
            themeColor: actionButtonOpts?.themeColor,
            text: actionButtonOpts?.text,
            loadingText: actionButtonOpts?.loadingText || actionButtonOpts?.stopText
        };
    }

    /**
     * Gets speech-to-text settings to pass to PromptBox.
     */
    private _getSpeechToTextSettings(options: IChatOptions): boolean | object | null {
        if (options.speechToText === false) {
            return false;
        }

        if (utilsService.isObject(options.speechToText)) {
            return options.speechToText as object;
        }

        return null;
    }

    /**
     * Gets file attachment settings to pass to PromptBox.
     */
    private _getFileAttachmentSettings(options: IChatOptions, enabled: boolean): boolean | object {
        if (!enabled) {
            return false;
        }

        if (utilsService.isObject(options.fileAttachment)) {
            return { showSpacer: true, _renderAffixLocation: "start", ...(options.fileAttachment as object) };
        }

        return { showSpacer: true, _renderAffixLocation: "start" };
    }

    private _getInternalFilesTemplate(options: IChatOptions): ((files: IFile[]) => string) | null {
        if (!options.filesTemplate || options.filesTemplate === renderFiles) {
            return null;
        }

        return (files: IFile[]) => options.filesTemplate!(files, false, options.messages, true);
    }

    /**
     * Handles PromptBox promptAction event.
     */
    private _promptBoxAction(e: any): void {
        e.preventDefault();

        const actionType = e.actionType;
        const text = e.value || "";
        const files = e.files || [];

        if (actionType === "stop") {
            const eventData = { generating: true };
            this.trigger(EVENTS.sendMessage, eventData);
            return;
        }

        if (!text.length && !files.length) {
            return;
        }

        const eventData = { message: { text, files } };
        this.trigger(EVENTS.sendMessage, eventData);
    }

    /**
     * Handles PromptBox fileSelect event.
     */
    private _promptBoxFileSelect(e: any): void {
        const files = this.promptBoxInstance?.files() || [];
        this.trigger(EVENTS.fileSelect, { files });
    }

    /**
     * Handles PromptBox fileRemove event.
     */
    private _promptBoxFileRemove(e: any): void {
        this.trigger(EVENTS.fileRemove, { file: e.file, files: e.files });
    }

    /**
     * Attaches event handlers to message box elements.
     */
    private _attachEvents(): void {
        this.wrapper!
            .on(CLICK + NS, `[${REFERENCES.replyMessageCloseButton}]`, this._replyMessageCloseButtonClick.bind(this))
            .on(CLICK + NS, DOT + STYLES.suggestion, this._suggestionClick.bind(this))
            .on(CLICK + NS, `[${REFERENCES.leftScrollButton}]`, this._leftScrollButtonClick.bind(this))
            .on(CLICK + NS, `[${REFERENCES.rightScrollButton}]`, this._rightScrollButtonClick.bind(this))
            .on(CLICK + NS, this._getCustomSendButtonSelector(), this._customSendButtonClick.bind(this))
            .on(INPUT + NS, this._getCustomInputSelector(), this._customInputChange.bind(this))
            .on(KEYDOWN + NS, this._getCustomInputSelector(), this._customInputKeyDown.bind(this))
            .on(CHANGE + NS, `${DOT}${STYLES.messageBox} input[type='file']`, this._customFileInputChange.bind(this));

        this._attachDragToScrollEvents();
    }

    /**
     * Attaches drag-to-scroll events for suggestions.
     */
    private _attachDragToScrollEvents(): void {
        const scrollContainer = this.wrapper!.find(DOT + STYLES.suggestionsScroll);
        if (!scrollContainer.length) {
            return;
        }

        this._dragHandler = domUtilsService.createDragToScrollHandler(
            scrollContainer,
            {
                namespace: NS + ".chatMessageBoxDrag",
                captureElement: this.chatElement
            }
        );
        this._dragHandler.attach();
    }

    /**
     * Gets the current files from the PromptBox.
     */
    getFiles(): any[] {
        if (this._usesCustomTemplate()) {
            return [...this.customFiles];
        }

        return this.promptBoxInstance?.files() || [];
    }

    /**
     * Sets files on the PromptBox.
     */
    setFiles(files: any[]): void {
        if (this._usesCustomTemplate()) {
            this.customFiles = [...files];
            return;
        }

        this.promptBoxInstance?.files(files);
    }

    /**
     * Clears all file attachments.
     */
    clearFiles(): void {
        if (this._usesCustomTemplate()) {
            this.customFiles = [];
            this.customMessageBox?.find("input[type='file']").val("");
            return;
        }

        this.promptBoxInstance?.clearFiles();
    }

    private _getPrefixContainer(): IKendoJQuery {
        const container = this._getMessageBoxContainer();
        let header = container.find(".k-prompt-box-header") as IKendoJQuery;

        if (header.length === 0) {
            header = $('<div class="k-prompt-box-header" ref-promptbox-header><div ref-promptbox-attachments-host></div></div>') as IKendoJQuery;
            container.prepend(header);
        }

        return header;
    }

    /**
     * Sets a reply message in the message box interface.
     */
    setReplyMessage(message: IMessage, isOwnMessage: boolean): void {
        const options = this.options as IChatOptions;
        const replyContainer = this.getReplyMessageContainer();
        const replyTemplate = options.messageReferenceTemplate({
            text: message.text,
            files: message.files,
            isOwnMessage,
            renderCloseButton: true,
            renderFileMenuButton: false
        });
        const prefixContainer = this._getPrefixContainer();

        prefixContainer.removeClass("k-hidden");

        if (replyContainer.length === 0) {
            prefixContainer.prepend(replyTemplate);
        } else {
            replyContainer.replaceWith(replyTemplate);
        }
    }

    /**
     * Removes the reply message from the message box interface.
     */
    removeReplyMessage(): void {
        const replyContainer = this.getReplyMessageContainer();
        if (replyContainer.length > 0) {
            replyContainer.remove();
            this._updateHeaderVisibility();
        }
    }

    private _updateHeaderVisibility(): void {
        const prefixContainer = this._getPrefixContainer();
        const files = this.getFiles() as any[];
        const hasFiles = files && files.length > 0;
        const hasReply = this.getReplyMessageContainer().length > 0;

        if (!hasFiles && !hasReply) {
            prefixContainer.addClass("k-hidden");
        }
    }

    /**
     * Gets the reply message container element.
     */
    getReplyMessageContainer(): IKendoJQuery {
        return this._getPrefixContainer().find(`[${REFERENCES.messageReferenceReplyWrapper}]`) as IKendoJQuery;
    }

    loading(value?: boolean): boolean | void {
        if (value === undefined) {
            return this._generating;
        }

        this._generating = value;

        if (this._usesCustomTemplate()) {
            this._setCustomLoadingState(value);
            return;
        }

        this.promptBoxInstance!.loading(value);
    }

    value(newValue?: string): string | void {
        if (newValue === undefined) {
            return this._getInputValue();
        }

        this._setInputValue(newValue);
    }

    // Event handlers
    private _input(e?: any): void {
        const currentValue = e?.value ?? this._getInputValue();
        this.trigger(EVENTS.input, { value: currentValue });
    }

    private _replyMessageCloseButtonClick(e: any): void {
        e.preventDefault();
        this.trigger(EVENTS.replyMessageCloseButtonClick);
    }

    private _suggestionClick(e: any): void {
        e.preventDefault();
        const options = this.options as IChatOptions;
        const suggestion = $(e.target).closest(DOT + STYLES.suggestion);
        const text = suggestion.text();

        if (!text.length) {
            return;
        }

        this.trigger(EVENTS.suggestionClick, { text, behavior: options.suggestionsBehavior || "send" });
        if (options.suggestionsBehavior === "insert") {
            this._setInputValue(text);
        }
    }

    private _getInputValue(): string {
        if (this._usesCustomTemplate()) {
            return this.customInput?.val() as string || "";
        }

        return this.promptBoxInstance!.value();
    }

    private _setInputValue(value: string): void {
        if (this._usesCustomTemplate()) {
            this.customInput?.val(value);
            return;
        }

        this.promptBoxInstance!.value(value);
    }

    private _usesCustomTemplate(): boolean {
        return !!this.customMessageBox?.length;
    }

    private _getMessageBoxContainer(): IKendoJQuery {
        if (this._usesCustomTemplate()) {
            return this.customMessageBox!;
        }

        return this.promptBoxInstance!.wrapper as IKendoJQuery;
    }

    private _findCustomInput(container: IKendoJQuery): IKendoJQuery {
        return container
            .find("textarea, input:not([type='hidden']):not([type='file']):not([type='button']):not([type='submit']):not([type='reset']):not([type='checkbox']):not([type='radio'])")
            .first() as IKendoJQuery;
    }

    private _findCustomSendButton(container: IKendoJQuery): IKendoJQuery {
        return container.find(`[${REFERENCES.sendButton}]`).first() as IKendoJQuery;
    }

    private _getCustomInputSelector(): string {
        return `${DOT}${STYLES.messageBox} textarea, ${DOT}${STYLES.messageBox} input:not([type='hidden']):not([type='file']):not([type='button']):not([type='submit']):not([type='reset']):not([type='checkbox']):not([type='radio'])`;
    }

    private _getCustomSendButtonSelector(): string {
        return `${DOT}${STYLES.messageBox} [${REFERENCES.sendButton}]`;
    }

    private _customSendButtonClick(e: any): void {
        if (!this._usesCustomTemplate()) {
            return;
        }

        const button = $(e.currentTarget);

        if (this.customSendButton?.length && !button.is(this.customSendButton)) {
            return;
        }

        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation?.();
        this._sendCustomTemplateMessage();
    }

    private _customInputChange(e: any): void {
        if (!this._usesCustomTemplate()) {
            return;
        }

        this._input({ value: $(e.currentTarget).val() || "" });
    }

    private _customInputKeyDown(e: any): void {
        if (!this._usesCustomTemplate()) {
            return;
        }

        const isEnter = e.key === "Enter" || e.which === 13 || e.keyCode === 13;

        if (!isEnter || this._generating || !this.customSendButton?.length) {
            return;
        }

        if (e.shiftKey && $(e.currentTarget).is("textarea")) {
            return;
        }

        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation?.();
        this._sendCustomTemplateMessage();
    }

    private _customFileInputChange(e: any): void {
        if (!this._usesCustomTemplate()) {
            return;
        }

        const input = e.currentTarget as HTMLInputElement;
        const selectedFiles = Array.from(input.files || []).map(file => ({
            uid: utilsService.guid(),
            name: file.name,
            size: file.size,
            extension: file.name.lastIndexOf(".") > -1 ? file.name.substring(file.name.lastIndexOf(".")).toLowerCase() : "",
            rawFile: file
        })) as IFile[];

        if (!selectedFiles.length) {
            return;
        }

        if (input.multiple) {
            this.customFiles = [...this.customFiles, ...selectedFiles];
        } else {
            this.customFiles = selectedFiles;
        }

        this.trigger(EVENTS.fileSelect, { files: [...this.customFiles] });
    }

    private _sendCustomTemplateMessage(): void {
        if (this._generating) {
            this.trigger(EVENTS.sendMessage, { generating: true });
            return;
        }

        const text = this._getInputValue().trim();
        const files = this.getFiles();

        if (!text.length && !files.length) {
            return;
        }

        this.trigger(EVENTS.sendMessage, { message: { text, files } });
        this._setInputValue("");
        this.clearFiles();
        this.customInput?.trigger("focus");
    }

    private _setCustomLoadingState(value: boolean): void {
        const messages = (this.options as IChatOptions).messages || {} as IChatMessages;

        if (!this.customSendButton?.length) {
            return;
        }

        const label = value
            ? (messages.sendButtonLoading || "Stop")
            : (messages.sendButton || "Send message");

        this.customSendButton.toggleClass(STYLES.generating, value);
        this.customSendButton.attr("aria-label", label);
        this.customSendButton.attr("title", label);

        if (value) {
            if (this._customSendButtonText?.trim()) {
                this.customSendButton.text(label);
            }
            return;
        }

        if (this._customSendButtonText?.trim()) {
            this.customSendButton.text(this._customSendButtonText);
        } else if (this._customSendButtonHtml !== undefined) {
            this.customSendButton.html(this._customSendButtonHtml);
        }
    }

    private _leftScrollButtonClick(e: any): void {
        e.preventDefault();
        const button = $(e.currentTarget);
        const scrollableElement = this.wrapper!.find(`${DOT}${STYLES.suggestionsScroll}`);
        const isRtl = (this.options as IChatOptions).dir === RTL;

        const position = domUtilsService.scrollByDelta(scrollableElement, isRtl ? SCROLLING_DELTA : -SCROLLING_DELTA);

        this.wrapper!.find(`[${REFERENCES.rightScrollButton}]`).removeClass(STYLES.disabled);

        if (position.atStart) {
            button.addClass(STYLES.disabled);
        }
    }

    private _rightScrollButtonClick(e: any): void {
        e.preventDefault();
        const button = $(e.currentTarget);
        const scrollableElement = this.wrapper!.find(`${DOT}${STYLES.suggestionsScroll}`);
        const isRtl = (this.options as IChatOptions).dir === RTL;

        const position = domUtilsService.scrollByDelta(scrollableElement, isRtl ? -SCROLLING_DELTA : SCROLLING_DELTA);

        this.wrapper!.find(`[${REFERENCES.leftScrollButton}]`).removeClass(STYLES.disabled);

        if (position.atEnd) {
            button.addClass(STYLES.disabled);
        }
    }

    destroy(): void {
        if (this._dragHandler) {
            this._dragHandler.destroy();
        }

        if (this.promptBoxInstance) {
            this.promptBoxInstance.destroy();
        }

        this.customFiles = [];
        this.wrapper!.remove();
    }
}
